/**
 * ClientTimelineList — a linha do tempo unificada do Resumo do Cliente.
 *
 * Uma lista cronológica única com os SETE tipos de documento do cliente. Cada
 * item navega para a rota do seu próprio tipo (a rota vem pronta do agregador,
 * `ClientTimelineEntry.route`) — foi exatamente aqui que o histórico antigo
 * estava quebrado, mandando tudo para `/orcamentos/:id`, que não existe.
 *
 * O filtro por tipo só oferece os tipos que ESTE cliente tem: um chip
 * "Contrato (0)" clicável para uma lista vazia é ruído.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, FileText, History } from 'lucide-react';
import {
  timelineKindLabel,
  timelineStatusLabel,
  type ClientTimelineEntry,
  type ClientTimelineKind,
} from '@/lib/clientSummary';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (date: Date) =>
  date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

/** Cor do selo por tipo — o mesmo tipo tem sempre a mesma cor na lista. */
const KIND_CLASS: Record<ClientTimelineKind, string> = {
  quote: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  sale: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  service_order: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  contract: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400',
  receipt: 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-400',
  appointment: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400',
  nfse: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

/** Ordem estável dos chips de filtro (não depende do que o cliente tem). */
const KIND_ORDER: ClientTimelineKind[] = [
  'quote',
  'sale',
  'service_order',
  'contract',
  'receipt',
  'appointment',
  'nfse',
];

interface ClientTimelineListProps {
  entries: ClientTimelineEntry[];
}

export function ClientTimelineList({ entries }: ClientTimelineListProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ClientTimelineKind | 'all'>('all');

  // Só os tipos presentes viram chip, com a contagem real de cada um.
  const availableKinds = useMemo(() => {
    const counts = new Map<ClientTimelineKind, number>();
    for (const entry of entries) {
      counts.set(entry.kind, (counts.get(entry.kind) ?? 0) + 1);
    }
    return KIND_ORDER.filter((kind) => counts.has(kind)).map((kind) => ({
      kind,
      count: counts.get(kind) as number,
    }));
  }, [entries]);

  const visible = useMemo(
    () => (filter === 'all' ? entries : entries.filter((entry) => entry.kind === filter)),
    [entries, filter],
  );

  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-10 text-center">
          <History className="mb-3 h-12 w-12 text-muted-foreground/50" />
          <p className="font-medium">Nenhum documento ainda</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Orçamentos, vendas, ordens de serviço, contratos e recibos deste cliente aparecem aqui.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Filtro por tipo — só aparece quando há mais de um tipo para separar. */}
      {availableKinds.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            className="h-8 rounded-full px-3 text-xs"
            onClick={() => setFilter('all')}
          >
            Tudo ({entries.length})
          </Button>
          {availableKinds.map(({ kind, count }) => (
            <Button
              key={kind}
              variant={filter === kind ? 'default' : 'outline'}
              size="sm"
              className="h-8 rounded-full px-3 text-xs"
              onClick={() => setFilter(kind)}
            >
              {timelineKindLabel(kind)} ({count})
            </Button>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {visible.map((entry) => (
          <Card
            key={`${entry.kind}-${entry.id}`}
            className="cursor-pointer transition-all hover:border-primary/30 hover:shadow-md active:scale-[0.99]"
            onClick={() => navigate(entry.route)}
          >
            <CardContent className="flex items-center justify-between gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${
                      KIND_CLASS[entry.kind]
                    }`}
                  >
                    {timelineKindLabel(entry.kind)}
                  </span>
                  <Badge variant="secondary" className="text-[10px]">
                    {timelineStatusLabel(entry.kind, entry.status)}
                  </Badge>
                </div>
                <p className="mt-1 truncate text-sm font-medium">{entry.title}</p>
                <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3 shrink-0" />
                  {formatDate(entry.date)}
                </div>
              </div>
              {/* Compromisso não tem valor — não inventamos "R$ 0,00". */}
              {entry.amount !== null && (
                <span className="shrink-0 text-sm font-semibold text-primary">
                  {formatCurrency(entry.amount)}
                </span>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {visible.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <FileText className="mb-3 h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhum documento deste tipo.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
